import { rgba } from 'polished';
import { FocusEvent, useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';

const Container = styled.div`
	border: none;
	padding: 10px;
	font-size: 16px;
	line-height: 20px;
	font-family: inherit;
	width: 100%;
	-webkit-appearance: none;
	appearance: none;
	background: white;

	&:focus {
		outline: none;
		background: linear-gradient(${(props) => props.theme.colors.lightBlueLight}, white);
		box-shadow:
			0px 0px 0px 1px ${(props) => props.theme.colors.primary},
			0px 0px 0px 3px ${(props) => rgba(props.theme.colors.primary, 0.25)},
			0px 1px 2px ${(props) => props.theme.colors.lightBlueDark} inset;
	}

	border-radius: 6px;
	box-shadow:
		0px 0px 0px 1px ${(props) => props.theme.colors.lightBlue},
		0px 1px 2px ${(props) => rgba(props.theme.colors.lightBlue, 0.5)} inset;
`;

const Placeholder = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	line-height: 20px;
	padding: 10px;
	font-size: 16px;
	color: ${(props) => props.theme.colors.slateBlue};
	pointer-events: none;
`;

export function TextArea(props: {
	autoFocus?: boolean;
	minRows?: number;
	maxRows?: number;
	placeholder?: string;
	value?: string;
	onChange?: (text: string) => void;
	onFocus?: (event: FocusEvent<HTMLDivElement>) => void;
	onBlur?: () => void;
}) {
	const ref = useRef<HTMLDivElement>(null);

	const isCurrentRef = !!ref.current;

	useLayoutEffect(() => {
		if (ref.current?.innerText !== props.value) {
			ref.current!.innerText = props.value ?? '';
		}
	}, [isCurrentRef, props.value]);

	useLayoutEffect(() => {
		if (ref.current && props.autoFocus) {
			ref.current.focus();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCurrentRef]);

	return (
		<div style={{ position: 'relative' }}>
			<Container
				ref={ref}
				contentEditable
				suppressContentEditableWarning
				onInput={(event) => props.onChange?.(event.currentTarget.innerText)}
				tabIndex={-1}
				style={{
					minHeight: props.minRows === undefined ? undefined : `${props.minRows * 20}px`,
					maxHeight: props.maxRows === undefined ? undefined : `${props.maxRows * 20}px`,
				}}
			/>
			{!props.value && <Placeholder>{props.placeholder}</Placeholder>}
		</div>
	);
}
